/**
 * Settled — the durable answer to "did I win?".
 *
 * **This tab exists because `docs/10` §1 said not to build it, and §F24f
 * overruled that.** The objection was the right one: the receipt is a
 * notification, and Phase 7 as briefed would have shipped a product where the
 * answer expired after a six-second toast. The record it points at lives in
 * Settled / History / Positions, all of which are Phase 8 — so the toast would
 * have been the only place, sixty times an hour, and dismissing it would have
 * lost the answer.
 *
 * **It was in-session only, and Phase 8 finished it.** §F24f built it on
 * `StoreSet.settlements` alone — already subscribed, already deduped
 * `immutable` on `settlement_id`, already newest-first with no consumer, so the
 * tab cost a render and no endpoint. That argument covers this session and not a
 * reload. `useSettlementArchive` now merges the socket's rows with cursor-paged
 * `GET /portfolio/settlements`, which since §F28 is the engine's own log rather
 * than the static `openapi` example it used to be (§F27h).
 *
 * Every figure is the server's — `Net` above all. All six terms of the P&L
 * identity ride on the frame so a client can check it rather than compute it,
 * and `settlementIdentity()` is that check.
 */

import { memo, useMemo } from 'react'
import { Button, EmptyState, Glyph, Pill, Skeleton, Table } from '@/components/primitives'
import type { TableColumn } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatPriceString, formatShares, formatSignedUsdc, formatUsdc, formatUtcTime } from '@/lib/format'
import { useSettlementArchive } from './use-settlement-archive'
import type { Settlement } from '@/components/market/SettlementReceipt'

export interface SettledTableProps {
  className?: string
  /**
   * Fetch the archive as well as the session's receipts. Default on.
   *
   * Off is for a surface that must make no request at all — the hook is one
   * fetch on mount and one per "Load more", never a timer, but "never a timer"
   * and "never a request" are different promises and some callers need the
   * second one.
   */
  archive?: boolean
}

export const SettledTable = memo(function SettledTable({ className, archive = true }: SettledTableProps) {
  const { rows: sorted, loading, hasMore, loadingMore, loadMore, failed } = useSettlementArchive(archive)

  const columns = useMemo<TableColumn<Settlement>[]>(
    () => [
      {
        id: 'time',
        header: 'Settled',
        cell: (row) => {
          const at = numberOf(row, 'settled_at_ms')
          return at === null ? '—' : formatUtcTime(at)
        },
      },
      {
        id: 'market',
        header: 'Market',
        card: 'title',
        cell: (row) => (
          <span className="truncate text-body-sm" title={stringOf(row, 'market_id') ?? undefined}>
            {stringOf(row, 'market_id') ?? '—'}
          </span>
        ),
      },
      {
        id: 'outcome',
        header: 'Outcome',
        card: 'title',
        cell: (row) => {
          const outcome = stringOf(row, 'outcome')
          if (outcome === 'VOID' || outcome === null) return <Pill tone="neutral">VOID</Pill>
          return (
            <span className={cx('flex items-center gap-1', outcome === 'UP' ? 'text-up-text' : 'text-down-text')}>
              <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} />
              {outcome}
            </span>
          )
        },
      },
      {
        id: 'held',
        header: 'Held',
        numeric: true,
        // `shares_won` / `shares_lost`, never a net figure: `net_shares` is
        // "never the settlement input" and this column is what settled.
        cell: (row) => {
          const mine = stringOf(row, 'my_outcome')
          const shares = row.won === true ? stringOf(row, 'shares_won') : stringOf(row, 'shares_lost')
          if (!shares) return '—'
          return `${formatShares(shares)}${mine ? ` ${mine}` : ''}`
        },
      },
      {
        id: 'settle',
        header: 'Settle',
        numeric: true,
        cell: (row) => {
          const price = (row.settlement_price ?? null) as Record<string, unknown> | null
          const value = price?.value
          return typeof value === 'string' ? formatPriceString(value) : '—'
        },
      },
      {
        id: 'cost',
        header: 'Cost',
        numeric: true,
        cell: (row) => formatUsdc(stringOf(row, 'cost_basis') ?? '0.000000'),
      },
      {
        id: 'payout',
        header: 'Payout',
        numeric: true,
        cell: (row) => formatUsdc(stringOf(row, 'payout') ?? '0.000000'),
      },
      {
        id: 'net',
        header: 'Net',
        numeric: true,
        cell: (row) => {
          const net = stringOf(row, 'net_pnl') ?? '0.000000'
          const negative = net.trimStart().startsWith('-')
          return <span className={negative ? 'text-down-text' : 'text-up-text'}>{formatSignedUsdc(net)}</span>
        },
      },
    ],
    [],
  )

  if (loading) return <Skeleton className={cx('h-24 w-full', className)} />

  return (
    <div className={cx('flex flex-col', className)}>
      <Table
        label="settled"
        density="comfortable"
        columns={columns}
        rows={sorted as Settlement[]}
        rowKey={(row) => stringOf(row, 'settlement_id') ?? String(row.market_id)}
        empty={<EmptyState>Nothing settled yet.</EmptyState>}
      />
      {/*
        The archive failing must not blank the tab. Whatever arrived on the
        socket this session is still the honest answer to "did I win?", and
        saying only the older rows are missing is more useful than an empty
        table that looks like nothing ever settled.
      */}
      {failed ? (
        <p role="status" className="px-2 py-1_5 text-caption text-text-tertiary">
          Older settlements could not be loaded. What is below arrived on this
          connection.
        </p>
      ) : null}
      {hasMore ? (
        <div className="flex justify-center border-t border-border-subtle py-1_5">
          <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  )
})

function stringOf(row: Settlement, field: string): string | null {
  const value = row[field]
  return typeof value === 'string' ? value : null
}

function numberOf(row: Settlement, field: string): number | null {
  const value = row[field]
  return typeof value === 'number' ? value : null
}

